// Partner-facing affiliate dashboard (affiliate plan §6). One /summary fetch:
// per-currency balances (never summed across currencies), copyable
// /r/<slug> links, in-flight payout batches and the anonymised commission
// ledger. A 404 for an unenrolled account renders the info card. Former
// partners keep read-only records even after the program closes. Enrolled partners can access setup before their first earning.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Linking, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import * as Clipboard from 'expo-clipboard';
import * as DocumentPicker from 'expo-document-picker';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import Button from '@/components/Buttons/Button';
import Text from '@/components/Typography/Text';
import AffiliateSecureForm from './AffiliateSecureForm';
import { affiliateApi } from '@/lib/api/affiliate';
import { API_HOST } from '@/constants/config';
import { useTranslation } from '@/hooks/useTranslation';

const SHARE_HOST = /^https?:\/\//.test(API_HOST || '') ? API_HOST : 'https://gab.ai';

const money = (minor, currency) => `${((Number(minor) || 0) / 100).toFixed(2)} ${currency}`;
const day = (value) => (value ? new Date(value).toLocaleDateString() : '—');
const sourceLabel = source => ({ 'code-entered': 'Via code', 'link-prefilled': 'Via link', cookie: 'Via link', 'account-attribution': 'Saved referral' }[source] || '');
const paymentLabel = kind => ({ initial: 'Initial payment', renewal: 'Renewal', upgrade: 'Upgrade', reactivation: 'Reactivation' }[kind] || kind || '');
const stateLabel = entry => {
  if (entry.state === 'held' || entry.disputeStatus === 'open') return 'On hold';
  if (['immature', 'payable'].includes(entry.state)) return entry.matureAt && +new Date(entry.matureAt) <= Date.now() ? 'Ready' : 'Maturing';
  return ({ allocated: 'Reserved for payout', paid: 'Paid', reversed: 'Reversed' }[entry.state] || entry.state || '');
};

const createStyles = (colors, compact) => {
  const ink = colors.text_color_primary;
  const secondary = colors.text_color_secondary;
  const surface = colors.solid_color_block;
  const line = colors.border_color_secondary;
  return StyleSheet.create({
    container: { gap: 30, paddingTop: compact ? 16 : 28 },
    loading: { alignItems: 'center', justifyContent: 'center', paddingVertical: 80 },
    card: { padding: compact ? 16 : 24, borderRadius: 16, backgroundColor: surface, borderWidth: 1, borderColor: line },
    agreementCard: { gap: 18 },
    agreementActions: { alignSelf: 'flex-start', marginTop: 4 },
    errorCard: { gap: 10 },
    errorTitle: { color: ink, fontSize: 17, lineHeight: 24, letterSpacing: -0.3 },
    section: { gap: 14 },
    sectionHeading: { fontSize: 20, lineHeight: 26, letterSpacing: -0.4, color: ink },
    stats: { flexDirection: 'row' },
    stat: { flex: 1, alignItems: 'center', gap: 4 },
    statNumber: { fontSize: 20, lineHeight: 26, letterSpacing: -0.5, color: ink },
    statLabel: { fontSize: 12, lineHeight: 16, color: secondary, textAlign: 'center' },
    statHelp: { minHeight: 44, paddingHorizontal: 4, alignItems: 'center', justifyContent: 'center' },
    balanceHelp: { borderTopWidth: 1, borderTopColor: line, paddingTop: 14, marginTop: 10, gap: 6 },
    linkCard: {
      flexDirection: compact ? 'column' : 'row',
      alignItems: compact ? 'stretch' : 'center',
      borderWidth: 1,
      borderColor: line,
      borderRadius: 14,
      padding: 6,
      paddingLeft: 16,
      gap: 12,
      marginBottom: 10,
    },
    linkText: { flex: 1 },
    linkUrl: { color: ink, fontSize: 13, lineHeight: 18 },
    linkMeta: { color: secondary, fontSize: 12, lineHeight: 16, marginTop: 2 },
    copyButton: { minHeight: 40, paddingHorizontal: 18, justifyContent: 'center', borderRadius: 10, backgroundColor: ink },
    copyLabel: { color: colors.text_color_alt, fontSize: 13 },
    row: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 14, paddingVertical: 11 },
    rowKey: { color: ink, fontSize: 13, lineHeight: 18, flexShrink: 1 },
    rowMeta: { color: secondary, fontSize: 12, lineHeight: 16, marginTop: 2 },
    rowAmount: { fontSize: 13, lineHeight: 18, color: ink },
    empty: { color: secondary, fontSize: 13, lineHeight: 20 },
  });
};

const AffiliateProgram = ({ refreshKey }) => {
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const { t } = useTranslation();
  const [view, setView] = useState({ status: 'loading' });
  const [accepting, setAccepting] = useState(false);
  const [copied, setCopied] = useState('');
  const [reviewed, setReviewed] = useState(false);
  const [acceptedOpen, setAcceptedOpen] = useState(false);
  const [statement, setStatement] = useState(null);
  const [balanceHelp, setBalanceHelp] = useState(null);
  const [requestBusy, setRequestBusy] = useState(false);
  const [requestMessage, setRequestMessage] = useState('');
  const [secureForm, setSecureForm] = useState(null);
  const [bank, setBank] = useState(null);
  const [tax, setTax] = useState(null);
  const [taxFile, setTaxFile] = useState(null);
  const [taxBusy, setTaxBusy] = useState(false);
  const [taxMessage, setTaxMessage] = useState('');

  const styles = useMemo(
    () => createStyles(colors, !!isXS),
    [colors, isXS]
  );

  const load = useCallback(async () => {
    setReviewed(false);
    setAcceptedOpen(false);
    setStatement(null);
    setView({ status: 'loading' });
    try {
      const [summary, documents, bankDetails] = await Promise.all([affiliateApi.getSummary(), affiliateApi.getDocuments().catch(() => ({ error: true })), affiliateApi.getBankDetails().catch(() => ({ error: true }))]);
      setBank(bankDetails); setSecureForm(null);
      setTax(documents);
      setView({ status: 'ready', data: summary || {} });
    } catch (error) {
      setView({ status: error && error.status === 404 ? 'unavailable' : 'failed' });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const copyLink = useCallback((url) => {
    Clipboard.setStringAsync(SHARE_HOST + url).catch(() => {});
    setCopied(url);
  }, []);

  const accept = useCallback(async () => {
    const terms = view.data && view.data.terms;
    if (!terms || !terms.id || !terms.agreement?.hash || !reviewed || accepting) return;
    setAccepting(true);
    try {
      await affiliateApi.acceptAgreement(terms.id, terms.agreement.hash, terms.agreement.version);
      await load();
    } catch (error) {
      setView({ status: 'failed' });
    } finally {
      setAccepting(false);
    }
  }, [accepting, load, reviewed, view]);

  const chooseTaxFile = async () => {
    if (taxBusy) return;
    setTaxBusy(true); setTaxMessage('');
    try {
      const result = await DocumentPicker.getDocumentAsync({ type: ['application/pdf', 'image/jpeg', 'image/png'], multiple: false, copyToCacheDirectory: true });
      if (result.canceled || !result.assets?.[0]) return;
      const asset = result.assets[0];
      if (!asset.size || asset.size > 5 * 1024 * 1024) { setTaxFile(null); setTaxMessage('Choose a nonempty PDF, JPEG or PNG of 5 MB or less.'); return; }
      setTaxFile(asset);
    } catch { setTaxMessage('Unable to choose a document. Please retry.'); }
    finally { setTaxBusy(false); }
  };
  const uploadTaxFile = async () => {
    if (!taxFile || taxBusy) return;
    setTaxBusy(true); setTaxMessage('');
    try {
      await affiliateApi.uploadDocument(taxFile);
      setTaxFile(null);
      setTax(await affiliateApi.getDocuments());
      setTaxMessage('Your W-9 was submitted for review. Submission does not mean approval.');
    } catch { setTax({ error: true }); setTaxMessage('Unable to confirm submission. Refresh tax status before trying again.'); }
    finally { setTaxBusy(false); }
  };

  if (view.status === 'loading') {
    return (
      <View style={styles.loading}>
        <ActivityIndicator size="small" />
      </View>
    );
  }

  if (view.status === 'failed') {
    return (
      <View style={[styles.card, styles.errorCard]} role="alert" accessibilityRole="alert">
        <Text style={styles.errorTitle}>{t('settings.affiliate.failed')}</Text>
        <Button text={t('settings.affiliate.retry')} onPress={() => load()} />
      </View>
    );
  }

  const data = view.data || {};
  const terms = data.terms || null;
  const acceptedAgreement = data.acceptedAgreement || terms?.acceptedAgreement;
  const balances = Array.isArray(data.balances) ? data.balances : [];
  const batches = Array.isArray(data.batches) ? data.batches : [];
  const links = data.enrollment?.canParticipate === false ? [] : Array.isArray(data.links) ? data.links : [];
  const ledger = Array.isArray(data.ledger) ? data.ledger : [];
  const payoutEligibility = data.payoutEligibility;
  const openRequest = payoutEligibility?.request;
  const canRequestPayout = payoutEligibility && !payoutEligibility.demo && (openRequest ? openRequest.emailStatus !== 'sent' && openRequest.retryAfterSeconds === 0 : payoutEligibility.readyMinor >= payoutEligibility.minimumMinor);
  const requestPayout = async () => {
    setRequestBusy(true); setRequestMessage('');
    try {
      const result = await affiliateApi.requestPayout();
      setRequestMessage(result.request?.emailStatus === 'sent' ? 'Your request was emailed to ai@gab.com for review. This does not initiate a bank transfer.' : 'Your request is saved. Email notification is still pending; refresh status before retrying.');
    } catch (error) {
      setRequestMessage(error?.data?.error === 'minimum-balance-not-met' ? 'Your matured USD balance is below the $100 minimum. Refresh to see the current balance.' : 'Your request could not be confirmed. Refresh status before retrying. A saved request will not be duplicated.');
    } finally { await load(); setRequestBusy(false); }
  };

  // Only an explicit enrollment/program 404 hides the dashboard.
  if (view.status === 'unavailable') {
    return (
      <View style={styles.card}>
        <Text style={styles.empty}>{t('settings.affiliate.notAvailable')}</Text>
      </View>
    );
  }

  // Review a new offer without withholding previously earned records.
  const needsAcceptance = terms && (terms.requiresAcceptance ?? !terms.acceptedAt) && data.enrollment?.canParticipate !== false;
  const agreementCard = needsAcceptance ? (
      <View style={[styles.card, styles.agreementCard]}>
        <Text style={styles.errorTitle}>{t('settings.affiliate.acceptTitle')}</Text>
        <Text style={styles.empty}>{t('settings.affiliate.acceptDescription')}</Text>
        <Text style={styles.empty}>{terms.partnerSummary?.text}</Text>
        <Pressable accessibilityRole="link" onPress={() => Linking.openURL(SHARE_HOST + '/affiliate-program-agreement')}>
          <Text style={[styles.empty, { textDecorationLine: 'underline' }]}>Read the Affiliate Program Agreement · {terms.agreement?.version}</Text>
        </Pressable>
        <ScrollView style={{ maxHeight: 320, borderWidth: 1, borderColor: colors.border_color_secondary, borderRadius: 10, padding: 16 }}>
          <Text style={styles.empty}>{terms.agreement?.text}</Text>
        </ScrollView>
        <Pressable accessibilityRole="checkbox" accessibilityState={{ checked: reviewed }} aria-checked={reviewed} onPress={() => setReviewed(value => !value)} style={{ flexDirection: 'row', gap: 10 }}>
          <Text style={styles.rowKey}>{reviewed ? '☑' : '☐'} I have read and agree to this Affiliate Program Agreement and the individual offer shown above, including the incorporated Terms of Service.</Text>
        </Pressable>
        <View style={styles.agreementActions}>
          <Button
            variant="contrast"
            text={accepting ? t('settings.affiliate.accepting') : t('settings.affiliate.accept')}
            isDisabled={accepting || !reviewed || !terms.agreement?.hash}
            onPress={accept}
          />
        </View>
      </View>
    ) : null;

  return (
    <View style={styles.container}>
      {agreementCard}
      {ledger.some(entry => entry.reason === 'local-preview') && <View style={styles.card}><Text style={styles.errorTitle}>Demo activity</Text><Text style={styles.empty}>Sample conversions and payout records for preview only. No real purchases, commissions or bank transfers occurred.</Text></View>}
      {data.enrollment?.canParticipate === false && <View style={[styles.card, styles.agreementCard]}>
        <Text style={styles.errorTitle}>Affiliate participation: {data.enrollment.status}</Text>
        <Text style={styles.empty}>New referrals and commissions are unavailable. Your earned balances and statements remain accessible, subject to payment review.</Text>
        {(data.enrollment.notices || []).map((notice, index) => <Text key={index} style={styles.empty}>{day(notice.at)} · {notice.text}</Text>)}
      </View>}
      {acceptedAgreement && <View style={styles.section}>
        <Pressable accessibilityRole="button" accessibilityState={{ expanded: acceptedOpen }} aria-expanded={acceptedOpen} onPress={() => setAcceptedOpen(value => !value)}><Text style={styles.rowKey}>{acceptedOpen ? 'Hide' : 'View'} accepted agreement · {day(acceptedAgreement.acceptedAt)}</Text></Pressable>
        {acceptedOpen && <ScrollView style={[styles.card, { maxHeight: 320 }]}><Text style={styles.empty}>{acceptedAgreement.text}</Text></ScrollView>}
      </View>}
      {statement && <View style={[styles.card, styles.agreementCard]}>
        <Text style={styles.errorTitle}>Payout statement</Text>
        <Text style={styles.empty}>{statement}</Text>
        <Pressable accessibilityRole="button" onPress={() => setStatement(null)}><Text style={styles.rowKey}>Close statement</Text></Pressable>
      </View>}
      {!terms && data.enrollment?.canParticipate !== false && <View style={styles.card}><Text style={styles.empty}>{t('settings.affiliate.setupPending')}</Text></View>}
      {balances.length > 0 && (
        <View style={styles.section}>
          <Text style={styles.sectionHeading} accessibilityRole="header">{t('settings.affiliate.balances')}</Text>
          <View style={styles.card}>
            {balances.map((row) => (
              <View key={row.currency} style={styles.stats}>
                <View style={styles.stat}>
                  <Text style={styles.statNumber}>{money(row.readyMinor, row.currency)}</Text>
                  <Pressable style={styles.statHelp} accessibilityRole="button" accessibilityLabel={t('settings.affiliate.readyHelpLabel')} accessibilityState={{ expanded: balanceHelp === 'ready' }} aria-expanded={balanceHelp === 'ready'} onPress={() => setBalanceHelp(value => value === 'ready' ? null : 'ready')}>
                    <Text style={styles.statLabel}>{t('settings.affiliate.ready')} ⓘ</Text>
                  </Pressable>
                </View>
                <View style={styles.stat}>
                  <Text style={styles.statNumber}>{money(row.immatureFutureMinor, row.currency)}</Text>
                  <Pressable style={styles.statHelp} accessibilityRole="button" accessibilityLabel={t('settings.affiliate.maturingHelpLabel')} accessibilityState={{ expanded: balanceHelp === 'maturing' }} aria-expanded={balanceHelp === 'maturing'} onPress={() => setBalanceHelp(value => value === 'maturing' ? null : 'maturing')}>
                    <Text style={styles.statLabel}>{t('settings.affiliate.maturing')} ⓘ</Text>
                  </Pressable>
                </View>
                <View style={styles.stat}>
                  <Text style={styles.statNumber}>{money(row.heldMinor, row.currency)}</Text>
                  <Pressable style={styles.statHelp} accessibilityRole="button" accessibilityLabel={t('settings.affiliate.heldHelpLabel')} accessibilityState={{ expanded: balanceHelp === 'held' }} aria-expanded={balanceHelp === 'held'} onPress={() => setBalanceHelp(value => value === 'held' ? null : 'held')}>
                    <Text style={styles.statLabel}>{t('settings.affiliate.held')} ⓘ</Text>
                  </Pressable>
                </View>
              </View>
            ))}
            {balanceHelp && <View style={styles.balanceHelp}>
              <Text style={styles.rowKey}>{t(`settings.affiliate.${balanceHelp}`)}</Text>
              <Text style={styles.empty}>{t(`settings.affiliate.${balanceHelp}Explanation`)}</Text>
            </View>}
          </View>
        </View>
      )}
      {!needsAcceptance && terms && data.enrollment?.canParticipate !== false && payoutEligibility && <View style={styles.section}>
        <Text style={styles.sectionHeading} accessibilityRole="header">Request a payout</Text>
        <View style={[styles.card, { gap: 12 }]}>
          <Text style={styles.empty}>Minimum: $100 in matured USD commissions. Maturing and on-hold earnings do not count. Requests are reviewed before payment; tax and payment details must be verified.</Text>
          {payoutEligibility.demo ? <Text style={styles.empty}>Demo earnings cannot be submitted for payment.</Text> : openRequest ? <Text style={styles.empty}>{openRequest.emailStatus === 'sent' ? `Requested ${day(openRequest.requestedAt)} · ${money(openRequest.amountMinor, openRequest.currency)} · awaiting review` : 'Your request is saved, but email delivery is not confirmed. Wait two minutes and refresh status to retry the notification.'}</Text> : payoutEligibility.readyMinor < payoutEligibility.minimumMinor && <Text style={styles.empty}>{money(payoutEligibility.minimumMinor - payoutEligibility.readyMinor, 'USD')} more in matured commissions needed.</Text>}
          <View style={{ alignSelf: 'flex-start' }}><Button text={requestBusy ? 'Please wait…' : openRequest ? openRequest.emailStatus === 'sent' ? 'Payout requested' : 'Retry notification' : 'Request payout'} onPress={requestPayout} isDisabled={requestBusy || !canRequestPayout} /></View>
          {!!((openRequest && openRequest.emailStatus !== 'sent') || requestMessage) && <Pressable accessibilityRole="button" onPress={load}><Text style={styles.rowKey}>Refresh payout status</Text></Pressable>}
          {!!requestMessage && <Text style={styles.empty}>{requestMessage}</Text>}
        </View>
      </View>}
      {!needsAcceptance && terms && data.enrollment?.canParticipate !== false && <View style={styles.section}>
        <Text style={styles.sectionHeading} accessibilityRole="header">Referral activity</Text>
        <View style={[styles.card, { gap: 12 }]}>
          <Text style={styles.rowKey}>Recorded link clicks: {data.tracking?.clicks || 0} · Recorded subscription payments: {data.tracking?.payments || 0}</Text>
          <Text style={styles.empty}>All-time records. Clicks may include repeat visits; payment records are not unique subscriber counts.</Text>
        </View>
      </View>}
      {!needsAcceptance && terms && links.length === 0 && data.enrollment?.canParticipate !== false && <View style={styles.card}>
        <Text style={styles.empty}>Your affiliate code and share link are being prepared. Contact ai@gab.com if you need help.</Text>
      </View>}
      {!needsAcceptance && links.length > 0 && (
        <View style={styles.section}>
          <Text style={styles.sectionHeading} accessibilityRole="header">{t('settings.affiliate.links')}</Text>
          {links.map((link) => (
            <View key={link.slug} style={styles.linkCard}>
              <View style={styles.linkText}>
                <Text numberOfLines={1} style={styles.linkUrl}>{SHARE_HOST + link.url}</Text>
                {(link.code || link.label) ? (
                  <Text numberOfLines={1} style={styles.linkMeta}>
                    {[link.code ? `Code: ${link.code}` : '', link.label, link.kind].filter(Boolean).join(' · ')}
                  </Text>
                ) : null}
              </View>
              {link.code && <Pressable style={styles.copyButton} accessibilityRole="button" onPress={async () => { try { await Clipboard.setStringAsync(link.code); setCopied(`code:${link.code}`); } catch { setCopied(''); } }}><Text style={styles.copyLabel}>{copied === `code:${link.code}` ? 'Copied' : 'Copy code'}</Text></Pressable>}
              <Pressable style={styles.copyButton} onPress={() => copyLink(link.url)} accessibilityRole="button">
                <Text style={styles.copyLabel}>
                  {copied === link.url ? t('settings.affiliate.copied') : t('settings.affiliate.copyLink')}
                </Text>
              </Pressable>
            </View>
          ))}
        </View>
      )}
      {batches.length > 0 && (
        <View style={styles.section}>
          <Text style={styles.sectionHeading} accessibilityRole="header">{t('settings.affiliate.payouts')}</Text>
          <View style={styles.card}>
            {batches.map((batch, index) => (
              <View key={batch.id || `${batch.batchNo}-${index}`} style={styles.row}>
                <View style={{ flex: 1 }}>
                  <Text style={styles.rowKey} numberOfLines={1}>
                    {`${batch.batchNo} · ${day(batch.windowStart)} – ${day(batch.windowEnd)}`}
                  </Text>
                  <Text style={styles.rowMeta}>{batch.allocationStatus}</Text>
                  <Pressable accessibilityRole="button" onPress={async () => {
                    try {
                      const rows = await affiliateApi.getPayoutStatement(batch.id);
                      setStatement(rows.map(row => `${row.batchNo} · ${row.currency} · ${row.batchStatus}\nReserved: ${money(row.reservedMinor, row.currency)}\n${(row.rows || []).map(entry => `${day(entry.paidAt)} · ${entry.state} · ${money(entry.netMinor, row.currency)}`).join('\n')}`).join('\n\n'));
                    } catch { setStatement('Unable to load this statement. Please retry.'); }
                  }}><Text style={[styles.rowMeta, { textDecorationLine: 'underline' }]}>View statement</Text></Pressable>
                </View>
                <Text style={styles.rowAmount}>{money(batch.amountMinor, batch.currency)}</Text>
              </View>
            ))}
          </View>
        </View>
      )}
      {!needsAcceptance && terms && <View style={styles.section}>
        <Text style={styles.sectionHeading} accessibilityRole="header">Tax documents</Text>
        <View style={[styles.card, { gap: 14 }]}>
          {tax?.error ? <><Text style={styles.empty}>Unable to load tax status.</Text><Button text="Refresh tax status" onPress={async () => { try { setTax(await affiliateApi.getDocuments()); } catch { setTax({ error: true }); } }} /></> : <>
            <Text style={styles.rowKey}>W-9 status: {(tax?.taxStatus || 'missing').replaceAll('_', ' ')}</Text>
            <Text style={styles.rowKey}>Payee verification: {(tax?.payeeStatus || 'unset').replaceAll('_', ' ')}</Text>
            <Text style={styles.empty}>A reviewed W-9 and verified payee are required before payment. Do not email tax IDs or bank details.</Text>
            {(tax?.documents || []).map(document => <Text key={document.id} style={styles.empty}>W-9 · {document.status} · {day(document.createdAt)}</Text>)}
            {data.enrollment?.canParticipate !== false && ['missing', 'needs_correction'].includes(tax?.taxStatus || 'missing') && (tax?.uploadAvailable ? <>
              {secureForm === 'w9' ? <AffiliateSecureForm purpose="w9" onCancel={() => setSecureForm(null)} onSubmitted={async () => { setSecureForm(null); setTax(await affiliateApi.getDocuments()); }} /> : <View style={{ alignSelf: 'flex-start' }}><Button variant="contrast" text="Fill out W-9" onPress={() => { setTaxFile(null); setSecureForm('w9'); }} /></View>}
              <Text style={styles.empty}>Or upload your completed official W-9 as a PDF, JPEG or PNG, up to 5 MB.</Text>
              <View style={{ alignSelf: 'flex-start' }}><Button text="Choose W-9" onPress={chooseTaxFile} isDisabled={taxBusy} /></View>
              {taxFile && <><Text style={styles.rowKey}>{taxFile.name}</Text><View style={{ alignSelf: 'flex-start' }}><Button variant="contrast" text={taxBusy ? 'Uploading…' : 'Upload W-9'} onPress={uploadTaxFile} isDisabled={taxBusy} /></View></>}
            </> : <Text style={styles.empty}>Secure tax uploads are temporarily unavailable. Contact ai@gab.com for help.</Text>)}
          </>}
          {!!taxMessage && <Text style={styles.empty} accessibilityLiveRegion="polite">{taxMessage}</Text>}
        </View>
      </View>}
      {!needsAcceptance && terms && <View style={styles.section}>
        <Text style={styles.sectionHeading} accessibilityRole="header">Payment details</Text>
        <View style={[styles.card, { gap: 14 }]}>
          {bank?.error ? <><Text style={styles.empty}>Unable to load payment status.</Text><Button text="Refresh payment status" onPress={async () => { try { setBank(await affiliateApi.getBankDetails()); } catch { setBank({ error: true }); } }} /></> : <>
            <Text style={styles.rowKey}>Bank verification: {(bank?.status || 'unset').replaceAll('_', ' ')}</Text>
            {bank?.documentStatus === 'needs_correction' && <Text style={styles.rowKey}>Your payment instructions need correction. Submit updated details for review.</Text>}
            {!!bank?.maskedDescription && <Text style={styles.empty}>{bank.maskedDescription}</Text>}
            <Text style={styles.empty}>Submit your U.S. ACH or domestic wire instructions securely. An admin must independently verify the destination before payment. Changes reset verification and invalidate the previous payout destination.</Text>
            {data.enrollment?.canParticipate !== false && bank?.uploadAvailable && (secureForm === 'bank' ? <AffiliateSecureForm purpose="bank" onCancel={() => setSecureForm(null)} onSubmitted={async () => { setSecureForm(null); setBank(await affiliateApi.getBankDetails()); setTax(await affiliateApi.getDocuments()); }} /> : <View style={{ alignSelf: 'flex-start' }}><Button variant="contrast" text={bank?.status === 'unset' ? 'Add payment details' : 'Update payment details'} onPress={() => setSecureForm('bank')} /></View>)}
            {data.enrollment?.canParticipate !== false && !bank?.uploadAvailable && <Text style={styles.empty}>Secure payment setup is temporarily unavailable. Do not email bank details.</Text>}
          </>}
        </View>
      </View>}
      <View style={styles.section}>
        <Text style={styles.sectionHeading} accessibilityRole="header">{t('settings.affiliate.ledger')}</Text>
        <View style={styles.card}>
          {ledger.length === 0 && <Text style={styles.empty}>{t('settings.affiliate.noEarnings')}</Text>}
          {ledger.map((entry, index) => (
            <View key={entry.id || `ledger-${index}`} style={styles.row}>
              <View style={{ flex: 1 }}>
                <Text style={styles.rowKey} numberOfLines={1}>{`${day(entry.paidAt)} · ${entry.kind || ''}`}</Text>
                <Text style={styles.rowMeta}>{[paymentLabel(entry.paymentKind), sourceLabel(entry.attributionSource), stateLabel(entry)].filter(Boolean).join(' · ')}</Text>
                {entry.kind === 'earning' && Number.isFinite(entry.eligibleAmountMinor) && Number.isFinite(entry.processingFeeMinor) && <Text style={styles.rowMeta}>Revenue excluding tax: {money(entry.eligibleAmountMinor, entry.currency)} · Processing deduction: {money(entry.processingFeeMinor, entry.currency)}</Text>}
                {['immature', 'payable'].includes(entry.state) && entry.matureAt && <Text style={styles.rowMeta}>{+new Date(entry.matureAt) <= Date.now() ? 'Matured' : 'Matures'} {day(entry.matureAt)}</Text>}
              </View>
              <Text style={styles.rowAmount}>{money(entry.commissionMinor, entry.currency)}</Text>
            </View>
          ))}
        </View>
      </View>
    </View>
  );
};

export default AffiliateProgram;
